import { useState, useEffect } from "react";
import { MapPin, X, Navigation, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

interface GeolocationPromptProps {
  isOpen: boolean;
  onClose: () => void;
  onAllow: () => void;
  onDeny: () => void;
  mode: 'initial' | 'denied';
}

export default function GeolocationPrompt({
  isOpen,
  onClose,
  onAllow,
  onDeny,
  mode,
}: GeolocationPromptProps) {
  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="bottom" className="rounded-t-3xl">
        <SheetHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <MapPin className="h-8 w-8 text-primary" />
          </div>
          <SheetTitle className="text-xl">
            {mode === 'initial' ? 'Enable Location' : 'Location Access Required'}
          </SheetTitle>
          <SheetDescription className="text-base">
            {mode === 'initial' 
              ? 'Allow location access to automatically detect nearby restaurants when creating reviews.'
              : 'Location access was denied. You can still search for restaurants manually, or enable location in your browser settings.'}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-3">
          {mode === 'initial' ? (
            <>
              <Button 
                onClick={onAllow} 
                className="w-full gap-2"
                size="lg"
              >
                <Navigation className="h-5 w-5" />
                Allow Location Access
              </Button>
              <Button 
                onClick={onDeny} 
                variant="outline" 
                className="w-full"
                size="lg"
              >
                Not Now
              </Button>
            </>
          ) : (
            <>
              <Button 
                onClick={onClose} 
                className="w-full"
                size="lg"
              >
                Search Manually
              </Button>
              <Button 
                onClick={() => {
                  window.open('about:settings', '_blank');
                  onClose();
                }} 
                variant="outline" 
                className="w-full gap-2"
                size="lg"
              >
                <Settings className="h-5 w-5" />
                Open Browser Settings
              </Button>
            </>
          )}
        </div>

        <p className="mt-4 text-center text-xs text-muted-foreground">
          We only use your location to find nearby restaurants. You can change this anytime in settings.
        </p>
      </SheetContent>
    </Sheet>
  );
}

const LOCATION_PREFERENCE_KEY = 'kudoz_location_preference';

type LocationPreference = 'allowed' | 'denied' | 'not_asked';

export function useLocationPreference() {
  const [preference, setPreference] = useState<LocationPreference>('not_asked');

  useEffect(() => {
    const saved = localStorage.getItem(LOCATION_PREFERENCE_KEY);
    if (saved === 'allowed' || saved === 'denied') {
      setPreference(saved);
    }
  }, []);

  const savePreference = (pref: LocationPreference) => {
    localStorage.setItem(LOCATION_PREFERENCE_KEY, pref);
    setPreference(pref);
  };

  return { preference, savePreference };
}

export async function requestGeolocation(): Promise<GeolocationPosition | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => resolve(position),
      () => resolve(null),
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      }
    );
  });
}
